import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Health-plan API — bridge's NEW (v4) flow, method-for-method
 * (new-bridge/modules/health-plan/services/HealthPlanService.js). All reads are
 * plain GETs; the ONLY writes are create (POST /v4/hp/{pid}) and update
 * (PUT /v4/hp/{pid}/{hpId}) — publish is the same write with published:true in
 * the body, there is no separate endpoint.
 */
class HealthPlanService {
  constructor() {
    this.apiService = new APIService();
    // Side effect: installs String.prototype.format, which every URL template
    // below needs. Looks unused; is load-bearing.
    this.helper = new Helper();
  }

  /** The latest plan version — the tab's primary load. */
  fetchLatest(patientId, onResponse, onError) {
    const url = APIConfig.healthPlan.latest.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The latest, read once (getOnce): ALWAYS answers one of the callbacks. `get`
   * drops a 200 whose body isn't JSON on the floor, which a caller holding the
   * screen locked until the read lands cannot afford.
   */
  fetchLatestOnce(patientId, onResponse, onError) {
    const url = APIConfig.healthPlan.latest.format(patientId);

    return this.apiService.getOnce(url, onResponse, onError);
  }

  /** One specific plan version (a "Level N" opened from the history). */
  fetchById(patientId, hpId, onResponse, onError) {
    const url = APIConfig.healthPlan.byId.format(patientId, hpId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** The version list for the history panel (bridge pages 2 at a time). */
  fetchList(patientId, onResponse, onError) {
    const url = APIConfig.healthPlan.list.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Category tabs (Nutrition/Exercise/Medical/Meditation) — [{ id, name }]. */
  getCategories(onResponse, onError) {
    return this.apiService.get(APIConfig.healthPlan.categories, onResponse, onError);
  }

  /** Master items for a category — the sidebar's addable section list. */
  getItemsByCategory(categoryId, onResponse, onError) {
    const url = APIConfig.healthPlan.itemsByCategory.format(categoryId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Option autocomplete for an item. */
  searchOptions(itemId, query, onResponse, onError) {
    const url = APIConfig.healthPlan.optionSearch.format(itemId, query);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Full option payload (name/description/images/recipes) on pick. */
  getOptionDetail(optionId, onResponse, onError) {
    const url = APIConfig.healthPlan.optionDetail.format(optionId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Unit choices for the guideline + recipe-ingredient unit selects. */
  getIngredientUnits(onResponse, onError) {
    return this.apiService.get(APIConfig.healthPlan.ingredientUnits, onResponse, onError);
  }

  /**
   * The ingredient master list for a recipe's "+ Add ingredient" (bridge's
   * getIngredientList). A bare array of { id, name, category } — bridge fetches
   * the whole list once rather than searching, so we do too.
   */
  getIngredients(onResponse, onError) {
    return this.apiService.get(APIConfig.healthPlan.ingredientsAll, onResponse, onError);
  }

  /** The patient's food list (bridge's getFoodList; filter "all"). */
  getFoodList(patientId, filter, onResponse, onError) {
    const url = APIConfig.healthPlan.foodList.format(patientId, filter || "all");

    return this.apiService.get(url, onResponse, onError);
  }

  /** A category's section templates matching `query` (legacy FetchPlan.searchTemplate). */
  searchTemplates(categoryId, query, onResponse, onError) {
    const url = APIConfig.healthPlan.templateSearch.format(categoryId, encodeURIComponent(query));

    return this.apiService.get(url, onResponse, onError);
  }

  /** One section template, with its sections (legacy FetchPlan.templateById). */
  getTemplate(templateId, onResponse, onError) {
    const url = APIConfig.healthPlan.templateById.format(templateId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Instruction suggestions for a recipe ingredient (legacy Recipe.getinstruction). */
  searchInstructions(query, onResponse, onError) {
    const url = APIConfig.healthPlan.instructionSearch.format(encodeURIComponent(query));

    return this.apiService.get(url, onResponse, onError);
  }

  /** Master guidelines for a category — the "+ Add guideline" choices. */
  getGuidelinesByCategory(categoryId, onResponse, onError) {
    const url = APIConfig.healthPlan.guidelinesByCategory.format(categoryId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Truthy response = patient is on the v4 model; falsy = legacy plan (bridge's
   * checkMigration). phr has no legacy renderer, so a falsy answer routes to a
   * "manage it in Bridge" notice.
   */
  checkMigration(patientId, onResponse, onError) {
    const url = APIConfig.healthPlan.migrationCheck.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Auto-generate the nutrition plan (bridge's "Generate Healthplan"). A GET
   * with server-side effects — the caller re-fetches the plan after.
   *
   * getOnce, not get: `get` silently re-fires on any non-200, and a re-fired
   * generate writes twice (on a published latest it creates TWO new versions).
   * getOnce still refreshes an expired token on a 401 and tries once more. A 204
   * ("No metadata exists to generate hp") answers `undefined`.
   */
  generateNutrition(patientId, employeeId, onResponse, onError) {
    const url = APIConfig.healthPlan.generate.format(patientId, employeeId);

    return this.apiService.getOnce(url, onResponse, onError);
  }

  /**
   * Kick off PDF generation after a publish (bridge's generateHPPDF). postAgain:
   * a re-fired call would queue the PDF twice.
   */
  generatePdf(patientId, healthPlanId, author, onResponse, onError) {
    const body = { patientId, healthPlanId, author };

    return this.apiService.postAgain(
      APIConfig.healthPlan.pdfGenerate,
      body,
      onResponse,
      onError
    );
  }

  /** Poll for the generated PDF — responds { pdfURL } once it's ready. */
  getPdfStatus(patientId, healthPlanId, onResponse, onError) {
    const url = APIConfig.healthPlan.pdfStatus.format(patientId, healthPlanId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The publish notification trigger (bridge's whatsappEvent). Body:
   * { userId, phoneNumber, countryCode, event, traits } — all strings, event is
   * "HP_PUBLISHED" or "HP_3_PUBLISHED". postAgain: a re-fired send could
   * message the patient twice.
   */
  whatsappEvent(body, onResponse, onError) {
    return this.apiService.postAgain(APIConfig.whatsapp.event, body, onResponse, onError);
  }

  /** Shorten a URL for the SMS body — responds { secureShortURL }. */
  whatsappShort(body, onResponse, onError) {
    return this.apiService.postAgain(APIConfig.whatsapp.short, body, onResponse, onError);
  }

  /** Send the referral-feedback SMS (03XXAZ flow). postAgain — never twice. */
  sendSms(body, onResponse, onError) {
    return this.apiService.postAgain(APIConfig.sms.send, body, onResponse, onError);
  }

  /** The CRM's record for a phone number (bridge's fetchDetails) — what the
   *  disposition is filed against. */
  fetchDispositionDetails(phone, onResponse, onError) {
    const url = APIConfig.tentacle.fetchDetails.format(encodeURIComponent(phone));

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * File a CRM disposition (bridge's createDisposition): a GET carrying the
   * record's phoneNo / firstName / startsAt / endsAt / email /
   * preferredLanguage, the disposition text, and source=Bridge — verbatim,
   * except that values are URL-encoded and a missing one is sent empty (bridge
   * sent the word "undefined").
   */
  createDisposition(disposition, details, onResponse, onError) {
    const d = details || {};
    const query = [
      ["phone", d.phoneNo],
      ["name", d.firstName],
      ["disposition", disposition],
      ["startsAt", d.startsAt],
      ["endsAt", d.endsAt],
      ["email", d.email],
      ["preferredLanguage", d.preferredLanguage],
      ["source", "Bridge"],
    ]
      .map(([k, v]) => `${k}=${encodeURIComponent(v == null ? "" : v)}`)
      .join("&");

    return this.apiService.get(`${APIConfig.tentacle.disposition}?${query}`, onResponse, onError);
  }

  /** The patient's member mapping ({ memberId }) — bridge's 02XXAB CleverTap event. */
  memberMapping(patientId, onResponse, onError) {
    const url = APIConfig.tentacle.memberMapping.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Referral code -> doctor profile ({ name }) — the user-service lookup. */
  searchDoctorCode(code, onResponse, onError) {
    const url = APIConfig.user.verifyDocCode.format(code);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Create the first save of a new version (plan has no id yet).
   * postAgain, NEVER post: post()'s error path re-fires on ANY non-2xx
   * (newAPIService), and a re-fired create could write the version twice.
   */
  create(patientId, plan, onResponse, onError) {
    const url = APIConfig.healthPlan.create.format(patientId);

    return this.apiService.postAgain(url, plan, onResponse, onError);
  }

  /** Update an existing version (draft save or publish). */
  update(patientId, hpId, plan, onResponse, onError) {
    const url = APIConfig.healthPlan.update.format(patientId, hpId);

    return this.apiService.put(url, plan, onResponse, onError);
  }
}

export default HealthPlanService;
